/**
 * DefaultProcessField — pole „Domyślny obieg akceptacji" w oknie edycji typu rekordu (typ
 * dokumentu, typ zadania).
 *
 * Lista wyboru: „— brak —" (bez domyślnego procesu), opublikowane procesy kartoteki i — gdy zapisane
 * wskazanie nie ma odpowiednika na liście (proces wycofany z publikacji / usunięty) — opcja
 * „(wskazany proces niedostępny)", żeby lista nie pokazywała fałszywie pustego wyboru, mimo że stan
 * trzyma realne id.
 */

import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import type { DefaultProcessOption } from '@/lib/record-types/default-process';

export interface DefaultProcessFieldProps {
  processes: DefaultProcessOption[];
  /** Wskazany proces; `null` = brak domyślnego procesu. */
  value: string | null;
  onChange: (processId: string | null) => void;
  /** Lista procesów jeszcze się nie rozstrzygnęła — bez opcji „niedostępny", żeby nie migała. */
  loading?: boolean;
  /** Pole w oknie NOWEGO typu: po wyborze procesu pokazuje podpowiedź o liście typów procesu. */
  isCreate?: boolean;
  /** Komunikat błędu serwera pod polem. */
  error?: string;
  'data-testid'?: string;
}

export function DefaultProcessField({
  processes,
  value,
  onChange,
  loading = false,
  isCreate = false,
  error,
  'data-testid': testId,
}: DefaultProcessFieldProps) {
  const { t } = useTranslation();
  return (
    <FormField
      label={t('tenant.recordTypes.defaultProcess.label')}
      hint={
        isCreate && value !== null ? t('tenant.recordTypes.defaultProcess.createHint') : undefined
      }
      error={error}
    >
      <Dropdown
        value={value ?? ''}
        onChange={(v) => onChange(v || null)}
        data-testid={testId}
        options={[
          { value: '', label: t('tenant.recordTypes.defaultProcess.none') },
          ...processes.map((p) => ({ value: p.id, label: p.name })),
          ...(!loading && value !== null && !processes.some((p) => p.id === value)
            ? [{ value, label: t('tenant.recordTypes.defaultProcess.missing') }]
            : []),
        ]}
      />
    </FormField>
  );
}
